import React, { useState, useEffect, useRef } from 'react';
import { Users, Award, Star, Scissors } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { barbers } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Team = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            barbers.forEach((barber, index) => {
              setTimeout(() => {
                setVisibleCards((prev) => [...prev, barber.id]);
              }, index * 200);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="equipe" ref={sectionRef} className="py-24 bg-[#050505] relative overflow-hidden">
      {/* Animated background gradients */}
      <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-[#00D9FF]/10 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-1/2 bg-gradient-to-tl from-[#FF1493]/10 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '1s' }} />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 animate-fade-in">
          <div className="inline-block mb-6">
            <div className="flex items-center gap-3 px-6 py-3 rounded-full glass-effect border border-white/20 backdrop-blur-xl">
              <Users className="w-5 h-5 text-[#00D9FF] animate-pulse" />
              <span className="text-sm font-bold gradient-text tracking-widest">{t.team.badge}</span>
            </div>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-slide-in-left" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t.team.title} <span className="gradient-text">{t.team.titleAccent}</span>
          </h2>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto leading-relaxed animate-slide-in-right" style={{ animationDelay: '0.2s' }}>
            {t.team.subtitle}
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {barbers.map((barber, index) => (
            <Card
              key={barber.id}
              className={`group relative bg-[#121212] border-white/10 transition-all duration-700 overflow-hidden ${
                hoveredCard === barber.id ? 'scale-105 border-[#00D9FF]/50 shadow-2xl shadow-[#00D9FF]/30' : ''
              } ${
                visibleCards.includes(barber.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              onMouseEnter={() => setHoveredCard(barber.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                transitionDelay: visibleCards.includes(barber.id) ? '0ms' : `${index * 200}ms`
              }}
            >
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00D9FF]/0 via-[#5B7FFF]/0 to-[#FF1493]/0 group-hover:from-[#00D9FF]/20 group-hover:via-[#5B7FFF]/10 group-hover:to-[#FF1493]/20 transition-all duration-700" />
              
              {/* Animated border glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00D9FF] to-transparent animate-shimmer" />
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FF1493] to-transparent animate-shimmer" style={{ animationDelay: '0.5s' }} />
              </div>

              {/* Floating particles on hover */}
              {hoveredCard === barber.id && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-1 h-1 bg-gradient-to-r from-[#00D9FF] to-[#FF1493] rounded-full animate-float"
                      style={{
                        left: `${10 + i * 12}%`,
                        bottom: '10%',
                        animationDelay: `${i * 0.15}s`,
                        animationDuration: `${2 + i * 0.2}s`
                      }}
                    />
                  ))}
                </div>
              )}

              <CardContent className="p-0 relative z-10">
                {/* Photo with overlay effects */}
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={barber.image}
                    alt={barber.name}
                    className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 filter group-hover:brightness-110 ${
                      barber.id === 2 ? 'object-[center_20%]' : ''
                    }`}
                  />
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
                  
                  {/* Experience badge */}
                  <div className="absolute top-4 right-4 px-4 py-2 glass-effect border border-white/20 backdrop-blur-xl rounded-full transform translate-x-full group-hover:translate-x-0 transition-transform duration-500">
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-[#00D9FF]" />
                      <span className="text-white text-sm font-semibold">{language === 'en' ? barber.experienceEn : barber.experience}</span>
                    </div>
                  </div>

                  {/* Scissors icon floating */}
                  <div className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 transition-all duration-500 transform -translate-y-2 group-hover:translate-y-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00D9FF]/20 to-[#FF1493]/20 backdrop-blur-xl border border-white/20 flex items-center justify-center">
                      <Scissors className="w-6 h-6 text-[#00D9FF] animate-pulse" />
                    </div>
                  </div>
                </div>

                {/* Info section */}
                <div className="p-6">
                  {/* Name with gradient on hover */}
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#00D9FF] group-hover:to-[#FF1493] transition-all duration-500">
                    {barber.name}
                  </h3>
                  
                  {/* Specialty */}
                  <p className="text-[#00D9FF] font-semibold mb-2 flex items-center gap-2">
                    <Star className="w-4 h-4 animate-pulse" />
                    {language === 'en' ? barber.specialtyEn : barber.specialty}
                  </p>
                  
                  {/* Role description */}
                  <p className="text-gray-400 text-sm group-hover:text-gray-200 transition-colors duration-500">
                    {language === 'en' ? barber.roleEn : barber.role}
                  </p>

                  {/* Animated separator line */}
                  <div className="mt-4 h-1 bg-gradient-to-r from-transparent via-[#00D9FF] to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </div>
              </CardContent>

              {/* Corner decorations */}
              <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-[#FF1493] opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
